import { DialogComponent, DialogContent } from "@allocine/ui-components"
import { Input } from "@components/input"
import { ProgressBar } from "@components/progress-bar"
import { useSecurity } from "@components/security"
import { Link } from "@components/typography"
import { useSearchEntitiesQuery } from "@graphql/apollo"
import {
	type CompanySearchEntityFragment,
	type MovieSearchEntityFragment,
	type PersonSearchEntityFragment,
	SearchableEntityType,
	type SeriesEpisodeSearchEntityFragment,
	type SeriesSearchEntityFragment,
	type SeriesSeasonSearchEntityFragment,
	type TheaterSearchEntityFragment,
} from "@graphql/types"
import { useDebounce } from "@hooks/use-debounce"
import { useHotkey } from "@hooks/use-hotkey"
import { SearchIcon } from "@icons/search-icon"
import { useTheme } from "@mui/material/styles"
import { getGlobalEntityLinkAttrs } from "@utils/global-routes"
import clsx from "clsx"
import {
	type ChangeEvent,
	forwardRef,
	type ReactNode,
	useCallback,
	useImperativeHandle,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { useIntl } from "react-intl"
import { DEFAULT_SEARCH_OPTIONS } from "../../../config/searchEntities"
import useInjectGlobalVariables from "../../../hooks/use-inject-global-variables"
import { useDefaultBrand } from "../preferences"
import CompanyListItem from "./entity-list-item/company-list-item"
import MovieListItem from "./entity-list-item/movie-list-item"
import PersonListItem from "./entity-list-item/person-list-item"
import SeriesEpisodeListItem from "./entity-list-item/series-episode-item-list"
import SeriesListItem from "./entity-list-item/series-list-item"
import SeriesSeasonListItem from "./entity-list-item/series-season-list-item"
import TheaterListItem from "./entity-list-item/theater-list-item"

const OPEN_SHORTCUTS_KEYS = ["Control+Shift+F", "Control+Shift+f", "Command+Shift+F", "Command+Shift+f"]

const DEFAULT_ENTITY_TYPES = [
	SearchableEntityType.Movie,
	SearchableEntityType.Series,
	SearchableEntityType.Company,
	SearchableEntityType.Person,
]

const ENTITY_TYPES_PERMISSIONS: Partial<
	Record<SearchableEntityType, (securityContext: ReturnType<typeof useSecurity>) => boolean>
> = {
	[SearchableEntityType.Movie]: ({ canReadMovies }) => canReadMovies(),
	[SearchableEntityType.Series]: ({ canReadSeries }) => canReadSeries(),
	[SearchableEntityType.SeriesSeason]: ({ canReadSeries }) => canReadSeries(),
	[SearchableEntityType.SeriesEpisode]: ({ canReadSeries }) => canReadSeries(),
	[SearchableEntityType.Company]: ({ canReadCompany }) => canReadCompany(),
	[SearchableEntityType.Person]: ({ canReadPersons }) => canReadPersons(),
}

type SearchEntityResultByGraphQLType = {
	Movie: MovieSearchEntityFragment[]
	Person: PersonSearchEntityFragment[]
	Series: SeriesSearchEntityFragment[]
	Company: CompanySearchEntityFragment[]
	Theater: TheaterSearchEntityFragment[]
	SeriesSeason: SeriesSeasonSearchEntityFragment[]
	SeriesEpisode: SeriesEpisodeSearchEntityFragment[]
}

export type SearchEntity = SearchEntityResultByGraphQLType[keyof SearchEntityResultByGraphQLType][number]

type PossibleSearchEntitiesTypes = keyof SearchEntityResultByGraphQLType

export type ListItemProps = {
	entity: SearchEntity
}
export const ListItem = ({ entity }: ListItemProps) => {
	const defaultBrand = useDefaultBrand()
	const { __typename } = entity

	const className = "hover:no-underline"
	let listItem: ReactNode
	if (__typename === "Movie") listItem = <MovieListItem movie={entity} brand={defaultBrand} />
	else if (__typename === "Series") listItem = <SeriesListItem series={entity} brand={defaultBrand} />
	else if (__typename === "Person") listItem = <PersonListItem person={entity} brand={defaultBrand} />
	else if (__typename === "Company") listItem = <CompanyListItem company={entity} brand={defaultBrand} />
	else if (__typename === "Theater") listItem = <TheaterListItem theater={entity} brand={defaultBrand} />
	else if (__typename === "SeriesSeason") listItem = <SeriesSeasonListItem season={entity} brand={defaultBrand} />
	else if (__typename === "SeriesEpisode") listItem = <SeriesEpisodeListItem episode={entity} brand={defaultBrand} />

	if (!listItem) return null
	return (
		<Link className={className} to={getGlobalEntityLinkAttrs(entity).to}>
			{listItem}
		</Link>
	)
}

type ListProps = {
	index: number
	title: string
	entities: SearchEntity[]
	onItemClick: () => void
}
const List = ({ index, title, entities, onItemClick }: ListProps) => (
	<>
		<h2
			className={clsx("font-bold uppercase mx-4 pb-2 border-b border-gray-200", {
				"pt-2": index > 0,
			})}
		>
			{title}
		</h2>
		<ul className="grid grid-cols-3 py-1 gap-2 mt-4 p-4">
			{entities.map((entity, ix) => (
				<li
					key={`${entity.__typename}-${ix}`}
					className="overflow-hidden hover:bg-gray-200 hover:rounded hover:overflow-visible"
					onClick={onItemClick}
				>
					<ListItem entity={entity} />
				</li>
			))}
		</ul>
	</>
)

interface SearchBarRef {
	focus: () => void
}

interface SearchBarProps {
	show: boolean
	search: string
	onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void
	onOpen: () => void
}

const SearchBar = forwardRef<SearchBarRef, SearchBarProps>(({ show, search, onSearchChange, onOpen }, ref) => {
	const inputRef = useRef<HTMLInputElement | null>(null)

	useImperativeHandle(ref, () => ({
		focus: () => {
			// Focus the input and select its content when called
			inputRef.current?.focus()
			inputRef.current?.select()
		},
	}))

	return (
		<div className="h-[56px] z-20 border-b">
			<div
				className={clsx("h-full gap-2", {
					"px-2": !show,
					"px-4 flex bg-white ": show,
				})}
			>
				<div className="h-full w-full flex items-center justify-between gap-1">
					<Input
						Icon={<SearchIcon />}
						ref={inputRef}
						onFocus={() => inputRef.current?.select()}
						className={clsx(
							"h-full w-full border-none pl-2 truncate",
							{
								"text-lg": show,
								"sm:max-xl2:w-16": !show,
							},
							"!border-0",
						)}
						value={search}
						onChange={onSearchChange}
						onClick={onOpen}
						placeholder="Search across all entities (Ctrl+Shift+F)"
					/>
				</div>
			</div>
		</div>
	)
})

const GlobalSearchInput = ({
	entityTypes = DEFAULT_ENTITY_TYPES,
	limitPerEntity = 9,
}: {
	entityTypes?: SearchableEntityType[]
	limitPerEntity?: number
}) => {
	const theme = useTheme()
	const { formatNumber } = useIntl()
	const securityCtx = useSecurity()
	const [show, setShow] = useState(false)
	const [search, setSearch] = useState("")
	const [debouncedSearch, { pending }] = useDebounce(search)
	const searchBarRef = useRef<SearchBarRef>(null)

	const handleOpen = useCallback(() => {
		setShow(true)
		// Focus will be handled by useEffect below
	}, [])
	const handleClose = useCallback(() => {
		setShow(false)
		// Remove focus from input when the dialog closes
		searchBarRef.current?.focus()
	}, [])

	useHotkey(
		OPEN_SHORTCUTS_KEYS,
		useCallback(
			(_ev, hotkey) => {
				if (OPEN_SHORTCUTS_KEYS.includes(hotkey)) handleOpen()
			},
			[handleOpen],
		),
	)

	const availableEntityTypes = useMemo(
		() =>
			entityTypes.filter((entityType) => {
				if (!ENTITY_TYPES_PERMISSIONS[entityType]) return false
				const perm = ENTITY_TYPES_PERMISSIONS[entityType]
				return perm(securityCtx)
			}),
		[entityTypes, securityCtx],
	)

	const shouldFetch = show && availableEntityTypes.length > 0

	const { data, previousData, loading } = useInjectGlobalVariables(useSearchEntitiesQuery, {
		skip: !shouldFetch,
		variables: {
			search: debouncedSearch || null,
			limit: limitPerEntity,
			offset: 0,
			entityType: availableEntityTypes,
			options: DEFAULT_SEARCH_OPTIONS,
		},
	})

	const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
		setSearch(event.target.value)
		if (event.target.value) setShow(true)
	}

	const dataTarget = data ?? previousData
	const count = dataTarget?.searchEntities.totalCount ?? 0
	const searchEntities = dataTarget?.searchEntities?.nodes as SearchEntity[]

	const entitiesByIndex = useMemo(() => {
		if (!searchEntities) return undefined

		return Object.entries(
			searchEntities?.reduce(
				(acc, entity) => {
					if (!(entity.__typename in acc)) acc[entity.__typename] = []
					;(acc[entity.__typename] as SearchEntity[]).push(entity)
					return acc
				},
				{
					Movie: [],
					Person: [],
					Series: [],
					SeriesSeason: [],
					SeriesEpisode: [],
					Company: [],
					Theater: [],
				} as SearchEntityResultByGraphQLType,
			),
		).reduce(
			(a, [k, items]) => {
				if (items.length > 0) a[k as PossibleSearchEntitiesTypes] = items
				return a
			},
			{} as Record<PossibleSearchEntitiesTypes, SearchEntity[]>,
		)
	}, [searchEntities])

	useLayoutEffect(() => {
		if (show) {
			// When the dialog opens, focus the input field using the SearchBar's imperative handle.
			// requestAnimationFrame ensures the input is present in the DOM before focusing.
			requestAnimationFrame(() => {
				searchBarRef.current?.focus()
			})
		}
	}, [show])

	if (!availableEntityTypes.length) return null

	return show ? (
		<DialogComponent
			title={
				<SearchBar ref={searchBarRef} show={show} search={search} onSearchChange={handleSearchChange} onOpen={handleOpen} />
			}
			maxWidth="xl"
			disableEscapeKeyDown={true}
			onKeyDown={(e) => {
				if (e.key === "Escape") handleClose()
			}}
			fullWidth
			onClose={handleClose}
			open={show}
			slotProps={{
				paper: {
					sx: {
						margin: 0,
						position: "absolute",
						top: theme.spacing(2),
					},
				},
			}}
			// Disable dialog's autoFocus to prevent it from stealing focus from the input field.
			// We handle focusing the input manually when the dialog opens.
			autoFocus={false}
		>
			<ProgressBar
				isIndeterminate={loading || pending}
				color={pending ? "secondary" : "info"}
				className={clsx("mx-3", {
					"opacity-0": !(loading || pending),
				})}
			/>
			{entitiesByIndex && (
				<div className="font-bold mx-3 py-2 text-center">
					Max of {limitPerEntity} results / {formatNumber(count)}&nbsp;
					{count > 10000 ? "+" : ""}match{count > 1 ? "es" : ""} for entities types:&nbsp;
					{Object.values(availableEntityTypes).join(", ")}...
				</div>
			)}
			<DialogContent>
				<div
					className={clsx({
						"pb-3": count > 0,
					})}
				>
					{/* Dropdown */}
					{entitiesByIndex && (
						<div className="overflow-hidden">
							{Object.entries(entitiesByIndex).map(([typename, entities], ix) => (
								<List
									index={ix}
									key={`${typename}-${ix}`}
									title={`${typename} (top  ${entities.length} / ${limitPerEntity})`}
									entities={entities}
									onItemClick={handleClose}
								/>
							))}
						</div>
					)}
				</div>
			</DialogContent>
		</DialogComponent>
	) : (
		<SearchBar ref={searchBarRef} show={show} search={search} onSearchChange={handleSearchChange} onOpen={handleOpen} />
	)
}

export default GlobalSearchInput
